// public/v3/src/lib/nav-badge-counts.js - eager chrome badge counts.
//
// Top-level nav tabs are not mounted until opened, so page-local badge effects
// cannot populate the chrome on first load. Keep the count predicates here in
// sync with the tab pages and let the app shell hydrate badges immediately.

// At-risk assessment badge lives in a CommonJS sibling (require-testable).
// Import (not just `export ... from`): a bare re-export forwards the name to
// consumers WITHOUT binding it in this module's scope, and the atRisk badge
// below calls it locally — that exact drift shipped as a silent ReferenceError
// swallowed by safeBadge (2026-07-09).
import { countAtRiskAssessmentBadge, hasOpenAssessmentRisk } from './assessment-risk';
export { countAtRiskAssessmentBadge, hasOpenAssessmentRisk };

import { chronicCount } from './ai-suggest';
import { suggestBhi } from './bhi-suggest';
import { suggestPain } from './pain-suggest';
import { payerProgramBlocker } from './payer-rules';
import { monthlyMinutes, BILLING_TIME_TARGETS } from './push-emr';
import {
  isActivePatient,
  isWorkflowExcluded,
  isWorkflowPatient,
  isBhiEnrolledPatient,
  isUnenrolledFor,
} from './workflow';

const APCM_FALLBACK_ELIGIBLE = new Set(['99490', '99491', '99487', '99439', '99437', '99489']);

function rawOf(row) {
  return row && row.data && typeof row.data === 'object' ? row.data : row;
}

function patientProjection(row, bhiSettings) {
  const raw = rawOf(row);
  if (!raw || typeof raw !== 'object') return null;
  const chronic = chronicCount({ _raw: raw });
  return {
    id: row && row.id,
    raw,
    program: (raw.billing && raw.billing.program) || '',
    consent: raw.consent || 'pending',
    chronic,
    payerClass: raw.payerClass || '',
    bhiSuggestion: suggestBhi(raw, bhiSettings || {}),
    painSuggestion: suggestPain(raw),
  };
}

function isDeclinedFor(raw, programFilter) {
  if (programFilter === 'bhi') return !!(raw && raw.bhi && raw.bhi.declined);
  return raw && raw.consent === 'declined';
}

function isBhiSuggestedCandidate(p, programFilter, bhiEnabled) {
  return !!(bhiEnabled && (programFilter === 'bhi' || programFilter === 'all') && p?.bhiSuggestion?.eligible);
}

// Per-path payer gate: each onboarding path leads to a concrete billed code
// (CCM → 99490 family, BHI → 99484), so candidacy requires the patient's
// payer class to actually reimburse that code. Keeps Medicaid-only admits
// (CoCM/Health-Home panels) from flooding the CCM onboarding queue while
// commercial patients — whose plans do pay CCM CPTs — stay onboardable.
function passesOnboardThreshold(p, programFilter, bhiEnabled) {
  // Pain lens: candidacy is the chart's pain signal + a G3002-compatible
  // payer (mirrors onboard.jsx passesVisibleConditionThreshold).
  if (programFilter === 'pain') {
    return !!(p?.painSuggestion?.eligible) && !payerProgramBlocker(p.payerClass, 'G3002');
  }
  // PIN requires one qualifying serious high-risk condition, captured per
  // patient at enrollment, plus a G0023-compatible payer.
  if (programFilter === 'pin') {
    return p.chronic >= 1 && !payerProgramBlocker(p.payerClass, 'G0023');
  }
  const ccmOk = p.chronic >= 2 && !payerProgramBlocker(p.payerClass, '99490');
  const bhiOk = isBhiSuggestedCandidate(p, programFilter, bhiEnabled)
    && !payerProgramBlocker(p.payerClass, '99484');
  return ccmOk || bhiOk;
}

function isTargetBearingProgram(program) {
  if (!program) return false;
  return (BILLING_TIME_TARGETS[program] || 0) > 0;
}

function isApcmFallbackCandidateProgram(program) {
  return APCM_FALLBACK_ELIGIBLE.has(program) && isTargetBearingProgram(program);
}

function targetForProgram(program) {
  return BILLING_TIME_TARGETS[program] != null ? BILLING_TIME_TARGETS[program] : 20;
}

function isSkippedForMonth(raw, month) {
  const skipMap = raw && raw.skipMonth && typeof raw.skipMonth === 'object' ? raw.skipMonth : {};
  return !!(month && skipMap[month]);
}

function hasApcmDecisionForMonth(raw, month) {
  const map = raw && raw.apcmMonth && typeof raw.apcmMonth === 'object' ? raw.apcmMonth : {};
  return !!(month && map[month]);
}

function isAlreadyBilled(patientId, month, billedState) {
  const entry = month && billedState && billedState[month] && billedState[month][patientId];
  if (!entry) return false;
  if (entry && typeof entry === 'object') {
    const keys = Object.keys(entry);
    if (keys.length === 1 && keys[0] === 'bhi_bn') return false;
  }
  return true;
}

export function countOnboardBadge(rows, programFilter = 'ccm', bhiSettings = {}, bhiEnabled = false) {
  return (rows || []).reduce((n, row) => {
    const p = patientProjection(row, bhiSettings);
    if (!p) return n;
    if (!isActivePatient(p.raw)) return n;
    if (isWorkflowExcluded(p.raw)) return n;
    if (!isUnenrolledFor(p, programFilter)) return n;
    if (isDeclinedFor(p.raw, programFilter)) return n;
    if (!passesOnboardThreshold(p, programFilter, bhiEnabled)) return n;
    return n + 1;
  }, 0);
}

export function countApcmBadge(rows, month, billedState = {}, programFilter = 'ccm') {
  return (rows || []).reduce((n, row) => {
    const p = patientProjection(row);
    if (!p) return n;
    if (!isWorkflowPatient(p.raw)) return n;
    if (p.consent !== 'signed') return n;
    if (!isApcmFallbackCandidateProgram(p.program)) return n;
    if (programFilter === 'bhi' && !isBhiEnrolledPatient(p.raw)) return n;
    if (p.chronic < 2) return n;
    if (isSkippedForMonth(p.raw, month)) return n;
    if (monthlyMinutes(p.raw, month) >= targetForProgram(p.program)) return n;
    if (hasApcmDecisionForMonth(p.raw, month)) return n;
    if (isAlreadyBilled(p.id, month, billedState)) return n;
    return n + 1;
  }, 0);
}

// Orchestrates the eager chrome-badge hydration. The patient list is the
// CRITICAL fetch — if it fails, the badge counts are unknowable, so we surface
// an error (ok:false, badges:null) and the caller leaves the prior counts in
// place rather than resetting them to 0 and silently implying "nothing to do".
// Transient failures of the patient fetch are retried. The settings and
// billed-state fetches are non-critical: a failure there degrades to defaults
// so the badges still render.
//
// `ccmApi` is the { listPatients, getSettings, getBilledState } surface.
export async function hydrateNavBadges(ccmApi, opts) {
  const o = opts || {};
  const monthKey = o.monthKey;
  const programFilter = o.programFilter || 'ccm';
  const attempts = o.attempts != null ? o.attempts : 2;
  const retryMs = o.retryMs != null ? o.retryMs : 400;

  let rows;
  let lastErr = null;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      rows = await ccmApi.listPatients();
      lastErr = null;
      break;
    } catch (e) {
      lastErr = e;
      if (attempt < attempts - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, retryMs));
      }
    }
  }
  if (lastErr || !rows) {
    return { ok: false, error: lastErr || new Error('patient list unavailable'), badges: null };
  }

  const [settings, billedState] = await Promise.all([
    Promise.resolve().then(() => ccmApi.getSettings()).then((s) => s || {}, () => ({})),
    Promise.resolve().then(() => ccmApi.getBilledState()).then((b) => b || {}, () => ({})),
  ]);
  const enabled = settings.bhi_enabled === true;

  // Per-badge fault isolation: a count predicate throwing on a single bad
  // patient row must not reject the whole hydration (which would blank every
  // badge and trip the "couldn't refresh" banner even though the fetch
  // succeeded). The failed badge becomes null — the caller leaves the prior
  // count in place — and the error is logged, not swallowed, so the next such
  // bug is diagnosable rather than invisible.
  const safeBadge = (label, fn) => {
    try {
      return fn();
    } catch (e) {
      try {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[nav-badge] ' + label + ' count failed', e);
        }
      } catch (_) { /* logging must never throw */ }
      return null;
    }
  };

  return {
    ok: true,
    error: null,
    badges: {
      apcm: safeBadge('apcm', () => countApcmBadge(rows, monthKey, billedState, programFilter)),
      onboard: safeBadge('onboard', () => countOnboardBadge(rows, programFilter, settings, enabled)),
      atRisk: safeBadge('atRisk', () => countAtRiskAssessmentBadge(rows)),
    },
  };
}
